import { StreemAPI } from '@streem/api';
import { CameraSubject, streem } from 'streem-sdk-protobuf';
import { invariant } from '@streem/toolbox';
import { from } from 'rxjs';
import { exhaustMap, map, switchMap } from 'rxjs/operators';
import { StreemError } from '../../streem_error';
import { buildRoomInviteData, protobufMapper, } from '../../util/build_room_invitation';
import { createRoomStatusObservable, onRoomStatus, } from '../../util/create_room_status_observable';
import { logger } from '../../util/logger';
import { validateConfigShape } from '../../util/validate_config_shape';
import { ARController } from '../controllers/ar_controller';
import { RoomSession } from './room_session';
export class HostRoomSession extends RoomSession {
    static async initialize(config) {
        return new HostRoomSession(config, await super.buildWallSyncClient(config));
    }
    constructor(config, wallSyncClient) {
        super(config, wallSyncClient);
        this.config = config;
        validateConfigShape(['onEnd', 'localUserId', 'localDeviceId', 'remoteUserId', 'roomId'], config);
        try {
            this.ar = new ARController({
                ...this.controllerConfig,
                mediaController: this.media,
            });
        }
        catch (error) {
            throw new StreemError(`Failed to create ARController for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        try {
            this.roomInvitation = this.invitationController.outgoingInvitation.pipe(switchMap(async (roomInvitation) => {
                const { fromUserSid } = roomInvitation;
                invariant(fromUserSid, 'Room invitation missing sender fromUserSid');
                try {
                    const { user } = await StreemAPI.users.getUser(fromUserSid);
                    return [roomInvitation, user];
                }
                catch (error) {
                    logger.error(`Attempted to find user ${fromUserSid} and failed.`, error);
                    throw new StreemError('Cannot find user for roomInvitation');
                }
            }), map(([roomInvitation, user]) => buildRoomInviteData(roomInvitation, user)));
        }
        catch (error) {
            throw new StreemError(`Failed to build RoomInvitation for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        try {
            this.roomInvitationStatus = this.invitationController.outgoingInvitationStatus.pipe(map(roomInvitationStatus => roomInvitationStatus
                ? protobufMapper.roomInvitationStatusFromV1(streem.api.RoomInvitation.Status[roomInvitationStatus])
                : 'UNKNOWN'));
        }
        catch (error) {
            throw new StreemError(`Failed to update status of RoomInvitation for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        try {
            this.status = createRoomStatusObservable(this.room.roomStatus, this.room.localParticipantStatus, this.room.remoteParticipantStatus, this.invitationController.outgoingInvitationStatus);
        }
        catch (error) {
            throw new StreemError(`Failed to create Observable<RoomSessionStatus> for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
    }
    // Asks for device permissions using browser's navigator API during audio & video track setup;
    async createHostMediaTracks() {
        // connect to the room wall
        await this.connect();
        // create our tracks
        await this.media.createHostTracks();
        // only record host video once the call has begun
        const roomStatusSub = onRoomStatus({
            status: 'CONNECTED',
            source: this.status,
        })
            .pipe(exhaustMap(() => from(this.startRecordingHostVideo())))
            .subscribe(() => {
            logger.info(`room is connected. started recording host video`);
        }, err => {
            logger.error(`failed to start recording host video: ${err}`);
        });
        this.subscriptionManager.add(roomStatusSub);
    }
    async startRecordingHostVideo() {
        // connect to track provider
        await this.media.connect(this.config.roomId, this.config.localUserId);
    }
    // Call this method once you're ready to join the room.
    async sendInvitation() {
        logger.debug('Host sending invitation to Spectator');
        return await this.invitationController.createInvitation({
            fromUserId: this.config.localUserId,
            fromDeviceId: this.config.localDeviceId,
            toUserId: this.config.remoteUserId,
        });
    }
    // TODO: wire this up to 'end call' button prior to pro accepting invitation
    async cancelInvitation(invitationId) {
        logger.info('Host cancelling invitation');
        await this.invitationController.cancelInvitation(invitationId);
        await this.media.disconnect();
        await this.endSession();
    }
}
HostRoomSession.canSendCameraSubjects = [
    CameraSubject.CAMERA_SUBJECT_WORLD,
    CameraSubject.CAMERA_SUBJECT_PERSON,
];
//# sourceMappingURL=host_room_session.js.map